import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PLATFORMS, ICONS, LOGO_URL, platformInfo } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Menu, ChevronDown, LogOut, Megaphone } from "lucide-react";
import { cn } from "@/lib/utils";

function SidebarContent({ onNavigate }) {
  const { hasPermission, user } = useAuth();
  const location = useLocation();
  const [socialOpen, setSocialOpen] = useState(location.pathname.startsWith("/social"));

  const navItem = (to, label, Icon, testId) => (
    <NavLink
      key={to}
      to={to}
      end={to === "/dashboard"}
      onClick={onNavigate}
      data-testid={testId}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200",
          isActive ? "bg-blue-50 text-blue-700" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
        )
      }
    >
      <Icon className="h-[18px] w-[18px]" />
      {label}
    </NavLink>
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-5 h-16 border-b border-gray-100">
        <img src={LOGO_URL} alt="BEAUTWELLZ" className="h-9 w-9 rounded-lg object-cover" />
        <div className="leading-tight">
          <p className="font-display font-extrabold text-[15px] text-gray-900">BEAUTWELLZ</p>
          <p className="text-[10px] text-muted-foreground tracking-wide">MAKE YOUR BRAND</p>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {navItem("/dashboard", "Dashboard", ICONS.LayoutDashboard, "nav-dashboard")}
        {hasPermission("customers") && navItem("/customers", "Customers", ICONS.Users, "nav-customers")}
        {hasPermission("products") && navItem("/products", "Products", ICONS.Package, "nav-products")}
        {hasPermission("products") && navItem("/inventory", "Inventory", ICONS.Boxes, "nav-inventory")}
        {hasPermission("billing") && navItem("/billing", "Billing", ICONS.ReceiptText, "nav-billing")}
        {hasPermission("billing") && navItem("/proformas", "Quotations", ICONS.ClipboardList, "nav-proformas")}
        {hasPermission("billing") && navItem("/notes", "Credit/Debit Notes", ICONS.FileMinus2, "nav-notes")}
        {hasPermission("billing") && navItem("/delivery-challans", "Delivery Challans", ICONS.ScrollText, "nav-challans")}
        {hasPermission("billing") && navItem("/eway-bills", "E-Way Bills", ICONS.Truck, "nav-eway")}
        {hasPermission("billing") && navItem("/purchases", "Purchases", ICONS.ShoppingCart, "nav-purchases")}
        {hasPermission("billing") && navItem("/expenses", "Expenses", ICONS.Wallet, "nav-expenses")}
        {hasPermission("billing") && navItem("/payments", "Payments", ICONS.Banknote, "nav-payments")}
        {hasPermission("billing") && navItem("/suppliers", "Suppliers", ICONS.Contact, "nav-suppliers")}
        {user?.role === "admin" && navItem("/reports", "Reports", ICONS.BarChart3, "nav-reports")}
        {user?.role === "admin" && navItem("/planning", "Planning", ICONS.Target, "nav-planning")}
        {user?.role === "admin" && navItem("/annual-report", "Annual Report", ICONS.TrendingUp, "nav-annual-report")}
        {hasPermission("billing") && navItem("/bank-spending", "Bank & Spending", ICONS.Banknote, "nav-bank-spending")}
        {(hasPermission("documents") || hasPermission("documents_download")) && navItem("/documents", "Documents", ICONS.FolderArchive, "nav-documents")}
        {hasPermission("team") && navItem("/team", "Team", ICONS.UserCog, "nav-team")}
        {hasPermission("media") && navItem("/media", "Media", ICONS.ImageIcon, "nav-media")}

        {hasPermission("social") && <div className="pt-2">
          <button
            data-testid="nav-social-toggle"
            onClick={() => setSocialOpen((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            <span className="flex items-center gap-3"><Megaphone className="h-[18px] w-[18px]" /> Social Media</span>
            <ChevronDown className={cn("h-4 w-4 transition-transform", socialOpen && "rotate-180")} />
          </button>
          {socialOpen && (
            <div className="mt-1 ml-2 pl-3 border-l border-gray-100 space-y-1">
              {PLATFORMS.map((p) => (
                <NavLink
                  key={p.key}
                  to={`/social/${p.key}`}
                  onClick={onNavigate}
                  data-testid={`nav-social-${p.key}`}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all",
                      isActive ? "bg-pink-50 text-pink-600" : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                    )
                  }
                >
                  <p.icon className="h-4 w-4" style={{ color: p.color }} />
                  {p.label}
                </NavLink>
              ))}
            </div>
          )}
        </div>}

        {navItem("/calendar", "Calendar", ICONS.CalendarDays, "nav-calendar")}
        {navItem("/settings", "Settings", ICONS.Settings, "nav-settings")}
      </nav>

      <div className="p-3 border-t border-gray-100">
        <div className="flex items-center gap-3 px-2 py-2 rounded-xl bg-gray-50">
          <Avatar className="h-8 w-8">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-blue-600 text-white text-xs">{(user?.name || "U").slice(0, 1).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="text-[13px] font-medium text-gray-900 truncate">{user?.name}</p>
            <p className="text-[11px] text-muted-foreground capitalize truncate">{user?.role}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DashboardLayout({ children, title, subtitle, actions }) {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const mobileItems = [
    { to: "/dashboard", label: "Home", Icon: ICONS.LayoutDashboard, show: true, testId: "mnav-dashboard" },
    { to: "/customers", label: "Leads", Icon: ICONS.Users, show: hasPermission("customers"), testId: "mnav-customers" },
    { to: "/billing", label: "Billing", Icon: ICONS.ReceiptText, show: hasPermission("billing"), testId: "mnav-billing" },
    { to: "/calendar", label: "Calendar", Icon: ICONS.CalendarDays, show: true, testId: "mnav-calendar" },
  ].filter((i) => i.show).slice(0, 4);

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-100 flex-col z-40">
        <SidebarContent />
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 h-16 flex items-center justify-between gap-4 px-4 sm:px-6 backdrop-blur-xl bg-white/80 border-b border-gray-100">
          <div className="flex items-center gap-3 min-w-0">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72">
                <SidebarContent onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="min-w-0">
              <h1 className="font-display font-bold text-lg sm:text-xl text-gray-900 truncate">{title}</h1>
              {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {actions}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 pl-1 pr-2" data-testid="user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-pink-500 text-white text-xs">{(user?.name || "U").slice(0, 1).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <ChevronDown className="h-4 w-4 text-muted-foreground hidden sm:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground font-normal">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8 bw-fade-in">{children}</main>
      </div>

      {/* App-like bottom navigation (mobile only) */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-gray-100 flex items-stretch justify-around pb-[env(safe-area-inset-bottom)]" data-testid="mobile-bottom-nav">
        {mobileItems.map(({ to, label, Icon, testId }) => (
          <NavLink key={to} to={to} end={to === "/dashboard"} data-testid={testId}
            className={({ isActive }) => cn(
              "flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition-colors",
              isActive ? "text-blue-600" : "text-gray-500"
            )}>
            <Icon className="h-5 w-5" />
            {label}
          </NavLink>
        ))}
        <button onClick={() => setOpen(true)} data-testid="mnav-more"
          className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium text-gray-500">
          <Menu className="h-5 w-5" />
          More
        </button>
      </nav>
    </div>
  );
}
